import { Entitlements } from '../models/entitlements';
import { HELP_SECTIONS, HelpSection, HowTo, SECTION_ROUTES } from './types';

/**
 * Listing, searching and grouping for the help window.
 *
 * Deliberately no Fuse.js: with a handful of How Tos, plain token matching is
 * enough, and it keeps the POC free of a dependency we'd only be judging by
 * proxy. Swap this out when the catalog outgrows it — nothing outside this
 * file knows how matching works.
 */

export interface HelpAudienceContext {
  /** Role names the current user holds, e.g. ['Editor', 'Reporting']. */
  roleNames: string[];
  isExternal: boolean;
  /** What the organisation's plan includes. Fails closed while loading, so a
   *  gated How To stays hidden until the real answer arrives. */
  entitlements: Entitlements;
  /** Whether anyone has signed in on this device yet. Defaults to true: every
   *  screen inside the app has an active user, and only the profile picker
   *  does not. */
  hasActiveUser?: boolean;
}

/** Hide what the user can't do — a guide to a page they can't open is worse
 *  than no guide. */
export const visibleHowTos = (all: HowTo[], ctx: HelpAudienceContext): HowTo[] =>
  all.filter(h => {
    // Before anyone is identified, the only guides on offer are the ones whose
    // steps can actually be carried out there. Not "the Security ones": a guide
    // can be about PINs and still open by telling you to click your avatar, or
    // offer to take you to an Admin screen you cannot reach without signing
    // in. Roles are unknown here too, so the role gate would let all of that
    // through.
    if (ctx.hasActiveUser === false) return h.availableSignedOut === true;
    if (h.requiredRole && !ctx.roleNames.includes(h.requiredRole)) return false;
    if (h.entitlement && !ctx.entitlements[h.entitlement]) return false;
    if (ctx.isExternal) return h.audience === 'external' || h.audience === 'all';
    return h.audience !== 'external';
  });

export const howTosForRoute = (all: HowTo[], pathname: string): HowTo[] =>
  all.filter(h => h.routes.includes(pathname));

const tokenise = (s: string): string[] =>
  s.toLowerCase().split(/[^a-z0-9']+/i).filter(t => t.length > 1);

/**
 * People type questions, not keywords: "how do I make it longer". Filler like
 * this would otherwise sink an otherwise perfect match, because every
 * meaningful token still has to land (below).
 */
const STOP_WORDS = new Set([
  'how', 'do', 'does', 'did', 'the', 'an', 'my', 'it', 'is', 'are', 'was',
  'can', 'could', 'should', 'would', 'to', 'of', 'in', 'on', 'at', 'for',
  'and', 'or', 'with', 'from', 'this', 'that', 'there', 'make', 'get', 'got',
  'want', 'need', 'please', 'help',
]);

const meaningfulTokens = (query: string): string[] =>
  tokenise(query).filter(t => !STOP_WORDS.has(t));

/**
 * Scores a How To against the query's tokens. A token counts when it is a
 * prefix of a word in the field — "sess" finds "session", and a keyword of
 * "wrong time" is matched by either word on its own.
 */
const scoreHowTo = (howTo: HowTo, queryTokens: string[]): number => {
  const title = tokenise(howTo.title);
  const keywords = howTo.keywords.flatMap(tokenise);
  const summary = tokenise(howTo.summary);
  const section = tokenise(howTo.section);

  let score = 0;
  for (const token of queryTokens) {
    const hit = (words: string[]) => words.some(w => w.startsWith(token));
    if (hit(title)) score += 10;
    else if (hit(keywords)) score += 6;
    else if (hit(section)) score += 3;
    else if (hit(summary)) score += 2;
    else return 0; // every token must land somewhere
  }
  return score;
};

export const searchHowTos = (all: HowTo[], query: string): HowTo[] => {
  const tokens = meaningfulTokens(query);
  if (tokens.length === 0) return [];
  return all
    .map(howTo => ({ howTo, score: scoreHowTo(howTo, tokens) }))
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score || a.howTo.title.localeCompare(b.howTo.title))
    .map(r => r.howTo);
};

export interface HelpGroup {
  section: HelpSection;
  items: HowTo[];
}

/** Groups by section in left-menu order — the grouping is the point: it shows
 *  which part of the app a task lives in.
 *
 *  `first` lifts one section to the top, keeping the rest in order. Browsing
 *  passes the section for the page you are on, so what you are looking at is
 *  what you read first — without a second, duplicate list to say so. */
export const groupBySection = (items: HowTo[], first?: HelpSection): HelpGroup[] => {
  const order = first && HELP_SECTIONS.includes(first)
    ? [first, ...HELP_SECTIONS.filter(s => s !== first)]
    : [...HELP_SECTIONS];
  return order
    .map(section => ({ section, items: items.filter(h => h.section === section) }))
    .filter(g => g.items.length > 0);
};

/** The section whose page this is, if any. Sections need not have a page —
 *  Security has none — so this is often undefined, and nothing is hoisted. */
export const sectionForRoute = (pathname: string): HelpSection | undefined =>
  (Object.keys(SECTION_ROUTES) as HelpSection[]).find(s => SECTION_ROUTES[s] === pathname);
